import { Delete, Done, Download } from '@mui/icons-material';
import { Alert, IconButton, List, ListItem, Switch } from '@mui/material';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { PhotoBooth } from '../../../../../interfaces/interfaces';
import { PhotoBoothComponentInterface } from '../../../../../interfaces/pagebuilder';
import { fetchAndSetPhotobooths, setEvent } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../../services/helper';
import { deletePhotobooth, updateEvent, updatePhotobooth } from '../../../../../services/services';
import { getFileFromUrl } from '../../../../../utils/functions';
import { MediaPreview } from '../../../../account/Media/MediaPreview';
import VyTooltip from '../../../../common/VyTooltip';
import { PageBuilderSettingsSection, PageBuilderSettingsTable } from '../PageBuilderSettingsTable';
import { VYSTEMBLUE } from '../../../../../static/constants';
import Image from 'next/image';
interface PhotoBoothSettingsTableInterface {
  component: PhotoBoothComponentInterface;
  onChange: (c: PhotoBoothComponentInterface) => void;
}

const PhotoBoothSettingsTable = ({ component, onChange }: PhotoBoothSettingsTableInterface) => {
  const event = useSelector((state) => state.common.event);
  const photobooths = useSelector((state) => state.common.photobooths);
  const pendingPhotobooths = photobooths?.filter((pb) => !pb.approved);
  const approvedPhotobooths = photobooths?.filter((pb) => pb.approved);

  const { t } = useTranslation(['pagebuilder']);
  const dispatch = useDispatch();

  const [currentPhotobooth, setCurrentPhotobooth] = useState<PhotoBooth>();

  async function _approvePhotoBooth(id: string, approved: boolean) {
    if (!event) return;
    await updatePhotobooth(id, { approved });
    dispatch(fetchAndSetPhotobooths(event._id));
  }

  async function _deletePhotoBooth(id: string) {
    if (!event) return;
    await deletePhotobooth(id);
    dispatch(fetchAndSetPhotobooths(event._id));
  }
  async function updateAutoApprove(approved: boolean) {
    if (event) {
      const data = {
        modules: {
          photobooths: {
            settings: {
              autoApprove: approved,
            },
          },
        },
      };
      await updateEvent({ eventid: event._id, ...data });
      const updatedEvent = {
        ...event,
        modules: {
          ...event.modules,
          photobooths: {
            ...event.modules.photobooths,
            settings: {
              autoApprove: approved,
            },
          },
        },
      };
      dispatch(setEvent(updatedEvent));
    }
  }

  async function downloadAll() {
    if (!photobooths) return;
    const zip = new JSZip();
    photobooths.map(async (pb, i) => {
      const url = createDisplayLink(pb.file, false);
      const file = await getFileFromUrl(url, pb.user.name);
      zip.file(`${i + 1}- ${file.name}.png`, file);
      // zip.file function is not async and can't be awaited
      // if we generate file outside of this block, it will be empty
      if (i === photobooths.length - 1)
        zip.generateAsync({ type: 'blob', streamFiles: false }).then((content) => {
          saveAs(content, `${t('account:photobooth.photobooths')}.zip`);
        });
    });
  }

  const PhotoboothImageHolder = (pb: PhotoBooth) => (
    <div className="flex space-x-2 ">
      <div
        onClick={() => setCurrentPhotobooth(pb)}
        className="relative mr-1 border rounded-md border-slate-400"
        style={{ width: 60, height: 60, cursor: 'zoom-in' }}>
        <Image
          fill
          onError={(event) => (event.currentTarget.style.display = 'none')}
          src={createDisplayLink(pb.file)}
          className="object-cover rounded-md"
          alt="pending photo"
        />
      </div>
      <div className="flex flex-col items-start">
        <span style={{ color: 'var(--vystem-blue)' }}> {pb.user.name}</span>
        <span className="break-all"> {pb.user.email}</span>
      </div>
    </div>
  );

  const settingSections: PageBuilderSettingsSection[] = [
    {
      name: t('account:photobooth.photos-approval'),
      actions: [
        {
          icon: (
            <VyTooltip title={t('account:photobooth.download-all-pictures')}>
              <Download />
            </VyTooltip>
          ),
          onClick: () => {
            downloadAll();
          },
          disabled: photobooths.length < 1,
        },
      ],
      settings: [
        {
          label: t('account:photobooth.auto-approve'),
          content: <Switch defaultChecked={event?.modules?.photobooths?.settings?.autoApprove} onChange={(e) => updateAutoApprove(e.target.checked)} />,
        },
        {
          label: t('account:photobooth.pending-photobooths'),
          content:
            pendingPhotobooths.length > 0 ? (
              <List>
                {pendingPhotobooths.map((pb) => (
                  <div key={pb._id} className="flex flex-row">
                    <ListItem className="w-full">{PhotoboothImageHolder(pb)}</ListItem>
                    <ListItem style={{ flex: 0 }}>
                      <IconButton onClick={() => _approvePhotoBooth(pb._id, true)}>
                        <Done htmlColor={VYSTEMBLUE} />
                      </IconButton>
                    </ListItem>
                    <ListItem style={{ flex: 0 }}>
                      <IconButton
                        onClick={() => {
                          _deletePhotoBooth(pb._id);
                        }}>
                        <Delete htmlColor="#cf4829" />
                      </IconButton>
                    </ListItem>
                  </div>
                ))}
              </List>
            ) : (
              <Alert severity="info" className="mt-2">
                {t('account:photobooth.pending-empty')}
              </Alert>
            ),
        },
        {
          label: t('account:photobooth.approved-photobooths'),
          content:
            approvedPhotobooths.length > 0 ? (
              <List>
                {approvedPhotobooths.map((pb) => (
                  <div key={pb._id} className="flex flex-row">
                    <ListItem className="w-full">{PhotoboothImageHolder(pb)}</ListItem>
                    <ListItem style={{ flex: 0 }}>
                      <IconButton
                        onClick={() => {
                          _deletePhotoBooth(pb._id);
                        }}>
                        <Delete htmlColor="#cf4829" />
                      </IconButton>
                    </ListItem>
                  </div>
                ))}
              </List>
            ) : (
              <Alert severity="info" className="mt-2">
                {t('account:photobooth.approved-empty')}
              </Alert>
            ),
        },
      ],
    },
  ];

  return (
    <>
      <PageBuilderSettingsTable sections={settingSections} onChange={onChange} component={component} />
      {currentPhotobooth && <MediaPreview open media={{ type: 'image', file: currentPhotobooth.file }} onClose={() => setCurrentPhotobooth(undefined)} />}
    </>
  );
};
export default PhotoBoothSettingsTable;
